import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_API_BASE_URL}/api/gallery`;

const Gallery = () => {
  const [events, setEvents] = useState([]);
  const [eventName, setEventName] = useState("");
  const [images, setImages] = useState([]); // new images
  const [existingImages, setExistingImages] = useState([]); // old images
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileRef = useRef(null);

  /* ================= FETCH ================= */
  const fetchGallery = async () => {
    try {
      const res = await axios.get(API);
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  /* ================= ADD / UPDATE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eventName) return;

    const formData = new FormData();
    formData.append("eventName", eventName);

    // only append new images
    images.forEach((img) => formData.append("images", img));

    try {
      setLoading(true);

      if (editId) {
        await axios.put(`${API}/${editId}`, formData);
      } else {
        await axios.post(API, formData);
      }

      // ✅ RESET FORM COMPLETELY
      setEventName("");
      setImages([]);
      setExistingImages([]);
      setEditId(null);
      if (fileRef.current) fileRef.current.value = "";

      fetchGallery();
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= EDIT ================= */
  const handleEdit = (event) => {
    setEditId(event._id);
    setEventName(event.eventName);
    setExistingImages(event.images); // show existing images
    setImages([]); // new images only
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this gallery event?")) return;
    await axios.delete(`${API}/${id}`);
    fetchGallery();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* ================= ADD / EDIT FORM ================= */}
      <div className="bg-white p-6 rounded-lg shadow mb-10 max-w-2xl">
        <h2 className="text-lg font-semibold mb-4">
          {editId ? "Edit Gallery" : "Add Gallery"}
        </h2>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Event Name"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="border p-2 w-full mb-4 rounded"
            required
          />

          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setImages([...e.target.files])}
            className="mb-4"
          />

          <button
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            {loading
              ? "Saving..."
              : editId
              ? "Update Gallery"
              : "Save Gallery"}
          </button>
        </form>

        {/* NEW IMAGE PREVIEW */}
        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            {images.map((img, i) => (
              <img
                key={i}
                src={URL.createObjectURL(img)}
                alt="preview"
                className="h-24 w-full object-cover rounded"
              />
            ))}
          </div>
        )}

        {/* EXISTING IMAGES (EDIT MODE) */}
        {editId && existingImages.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold mb-2">
              Existing Images
            </h4>

            <div className="grid grid-cols-4 gap-3">
              {existingImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="existing"
                  className="h-24 w-full object-cover rounded"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ================= GALLERY LIST ================= */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          Gallery List
        </h2>

        {events.map((event) => (
          <div
            key={event._id}
            className="bg-white p-4 rounded-lg shadow mb-6"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">
                {event.eventName}
              </h3>

              <div className="flex gap-4">
                <button
                  onClick={() => handleEdit(event)}
                  className="text-blue-600 text-sm"
                >
                  Edit
                </button>

                <button
                  onClick={() => handleDelete(event._id)}
                  className="text-red-600 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {event.images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="gallery"
                  className="h-32 w-full object-cover rounded"
                />
              ))}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default Gallery;
